import { Minus, Plus, X } from 'lucide-react'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  MORNING_A11Y_CHANGE_EVENT,
  TEXT_SIZE_STEPS,
  applyAccessibilityToDocument,
  commitAccessibilitySettings,
  defaultAccessibilitySettings,
  readStoredAccessibility,
  type AccessibilitySettings,
} from '@/lib/accessibility'
import { cn } from '@/lib/utils'

const PANEL_ANIMATION_MS = 300

const headerGlassButtonClass =
  'inline-flex size-9 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-zinc-200 shadow-sm backdrop-blur-sm transition-all outline-none select-none hover:bg-white/15 hover:text-white focus-visible:ring-3 focus-visible:ring-white/25 aria-expanded:bg-white/15 aria-expanded:text-white'

const accessibilityIconClass =
  'inline-block size-4.5 shrink-0 bg-white opacity-95 [mask-image:url(/icons/accessibility.png)] [mask-size:contain] [mask-repeat:no-repeat] [mask-position:center] [-webkit-mask-image:url(/icons/accessibility.png)] [-webkit-mask-size:contain] [-webkit-mask-repeat:no-repeat] [-webkit-mask-position:center]'

const accessibilityPanelClass =
  'fixed inset-0 z-[100] flex w-screen items-center justify-center overflow-y-auto bg-ocean-deep text-zinc-200 shadow-sm duration-300'

const headerGlassMutedClass = 'text-zinc-400'

const headerGlassSubPanelClass =
  'rounded-lg border border-white/15 bg-white/6'

type AccessibilityToggleProps = {
  className?: string
  children?: ReactNode
  triggerClassName?: string
  iconClassName?: string
}

function AccessibilityToolRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
}: {
  id: string
  label: string
  description?: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <Label htmlFor={id} className="text-sm font-medium text-inherit">
          {label}
        </Label>
        {description ? (
          <p
            className={cn(
              'mt-0.5 text-xs/relaxed',
              headerGlassMutedClass,
            )}
          >
            {description}
          </p>
        ) : null}
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={label}
        className="border-white/20 data-unchecked:bg-white/20 dark:data-unchecked:bg-white/20"
      />
    </div>
  )
}

export function AccessibilityToggle({
  className,
  children,
  triggerClassName,
  iconClassName,
}: AccessibilityToggleProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [settings, setSettings] = useState<AccessibilitySettings>(() =>
    readStoredAccessibility(),
  )

  const textSize = TEXT_SIZE_STEPS[settings.textSizeIndex] ?? 100

  const closePanel = () => {
    setOpen(false)
  }

  const togglePanel = () => {
    setOpen((current) => {
      if (!current) {
        // Dismiss the mobile nav overlay (or legacy drawer) so this panel
        // isn't trapped underneath / competing at the same stacking level.
        const overlayClose = document.querySelector<HTMLButtonElement>(
          '.sunrise-overlay button[aria-label="Close menu"]',
        )
        if (overlayClose) {
          overlayClose.click()
        } else {
          document
            .querySelector<HTMLButtonElement>(
              'button[aria-label="Close menu"][aria-controls]',
            )
            ?.click()
        }
      }
      return !current
    })
  }

  // Keep the panel mounted through the exit slide so it can animate out.
  useLayoutEffect(() => {
    if (open) {
      setMounted(true)
      return
    }

    if (!mounted) return

    const closeTimer = setTimeout(() => {
      setMounted(false)
    }, PANEL_ANIMATION_MS)

    return () => clearTimeout(closeTimer)
  }, [open, mounted])

  useEffect(() => {
    applyAccessibilityToDocument(settings)
  }, [settings])

  useEffect(() => {
    const onAccessibilityChange = (event: Event) => {
      const detail = (event as CustomEvent<AccessibilitySettings>).detail
      if (!detail) return
      setSettings(detail)
    }

    window.addEventListener(MORNING_A11Y_CHANGE_EVENT, onAccessibilityChange)
    return () => {
      window.removeEventListener(
        MORNING_A11Y_CHANGE_EVENT,
        onAccessibilityChange,
      )
    }
  }, [])

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePanel()
    }

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (triggerRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      closePanel()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const firstControl = panelRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    firstControl?.focus()
  }, [open])

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K],
  ) => {
    const next = commitAccessibilitySettings({ ...settings, [key]: value })
    setSettings(next)
  }

  const resetSettings = () => {
    const next = commitAccessibilitySettings(defaultAccessibilitySettings)
    setSettings(next)
  }

  return (
    <>
      <div
        className={cn(
          'flex w-fit shrink-0 items-center justify-end gap-2',
          className,
        )}
      >
        <button
          ref={triggerRef}
          type="button"
          aria-label="Accessibility options"
          aria-haspopup="dialog"
          aria-expanded={open}
          title="Accessibility options"
          className={cn(headerGlassButtonClass, triggerClassName)}
          onClick={togglePanel}
        >
          <span
            aria-hidden
            className={cn(accessibilityIconClass, iconClassName)}
          />
        </button>
        {children}
      </div>

      {mounted
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Accessibility"
              data-accessibility-menu=""
              data-state={open ? 'open' : 'closed'}
              className={cn(
                accessibilityPanelClass,
                open
                  ? 'animate-in slide-in-from-top fade-in-0 ease-out'
                  : 'animate-out slide-out-to-top fade-out-0 ease-in',
              )}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Close accessibility options"
                onClick={closePanel}
                className="absolute top-4 right-4 z-20 size-10 rounded-xl border border-white/20 bg-white/10 text-paper backdrop-blur-sm hover:bg-white/20 hover:text-white"
              >
                <X className="size-5" aria-hidden />
              </Button>

              <div className="mx-auto flex w-full max-w-lg flex-col gap-6 px-6 py-10">
                <div className="flex flex-col items-center gap-4 text-center">
                  <div className="font-heading-outfit text-lg font-medium tracking-[0.08em] text-inherit uppercase sm:text-xl">
                    Accessibility Controls
                  </div>
                  <p className={cn('max-w-sm text-lg/relaxed', headerGlassMutedClass)}>
                    At Morning Studio, we believe everyone should be able to use
                    the web with ease.
                  </p>
                </div>

                <Separator className="bg-white/15" />

                <div className="flex min-w-0 flex-col gap-5">
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-medium text-inherit">
                      Text size
                    </Label>
                    <div
                      className={cn(
                        'flex items-center justify-between gap-2 px-2 py-1.5',
                        headerGlassSubPanelClass,
                      )}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Decrease text size"
                        disabled={settings.textSizeIndex <= 0}
                        onClick={() =>
                          updateSetting(
                            'textSizeIndex',
                            Math.max(0, settings.textSizeIndex - 1),
                          )
                        }
                      >
                        <Minus className="size-3.5" aria-hidden />
                      </Button>
                      <span className="min-w-14 text-center text-sm font-medium text-inherit tabular-nums">
                        {textSize}%
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Increase text size"
                        disabled={
                          settings.textSizeIndex >= TEXT_SIZE_STEPS.length - 1
                        }
                        onClick={() =>
                          updateSetting(
                            'textSizeIndex',
                            Math.min(
                              TEXT_SIZE_STEPS.length - 1,
                              settings.textSizeIndex + 1,
                            ),
                          )
                        }
                      >
                        <Plus className="size-3.5" aria-hidden />
                      </Button>
                    </div>
                  </div>

                  <AccessibilityToolRow
                    id="a11y-high-contrast"
                    label="High contrast"
                    description="Boost contrast for text and controls."
                    checked={settings.highContrast}
                    onCheckedChange={(checked) =>
                      updateSetting('highContrast', checked)
                    }
                  />
                  <AccessibilityToolRow
                    id="a11y-reduce-motion"
                    label="Reduce motion"
                    description="Minimise animation and scrolling effects."
                    checked={settings.reduceMotion}
                    onCheckedChange={(checked) =>
                      updateSetting('reduceMotion', checked)
                    }
                  />
                  <AccessibilityToolRow
                    id="a11y-highlight-links"
                    label="Highlight links"
                    description="Underline and emphasise in-page links."
                    checked={settings.highlightLinks}
                    onCheckedChange={(checked) =>
                      updateSetting('highlightLinks', checked)
                    }
                  />
                  <AccessibilityToolRow
                    id="a11y-readable-font"
                    label="Readable font"
                    description="Use a simpler typeface across the page."
                    checked={settings.readableFont}
                    onCheckedChange={(checked) =>
                      updateSetting('readableFont', checked)
                    }
                  />
                </div>

                <Separator className="bg-white/15" />

                <Button
                  type="button"
                  variant="outline"
                  className="h-9 w-full rounded-xl border-white/15 bg-white/10 px-2.5 text-inherit shadow-none backdrop-blur-sm hover:bg-white/15 hover:text-white"
                  onClick={resetSettings}
                >
                  Reset to defaults
                </Button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
